import { Box, Button, Flex } from "@chakra-ui/react";
import { useCallback, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { TipTap, TipTapDocument } from "../../../../common/input/TipTap/TipTap";

interface Props {
  description: string;
  alwaysExpanded?: boolean;
  documents?: TipTapDocument[];
}

export const Intro = ({ description, alwaysExpanded = false, documents }: Props) => {
  const [showFullDescription, setShowFullDescription] = useState(alwaysExpanded);
  const [contentOverflows, setContentOverflows] = useState(false);
  const observerRef = useRef<ResizeObserver | null>(null);
  const { t } = useTranslation();

  const handleRef = useCallback((node: HTMLDivElement | null) => {
    if (observerRef.current) {
      observerRef.current.disconnect();
      observerRef.current = null;
    }
    if (node) {
      observerRef.current = new ResizeObserver(() => {
        setContentOverflows(node.scrollHeight > 128);
      });
      observerRef.current.observe(node);
    }
  }, []);

  const isCollapsed = !showFullDescription && !alwaysExpanded;

  const tipTapElement = useMemo(
    () => <TipTap content={description} editable={false} hasBorder={false} maxHeight="" padding="0" documents={documents} />,
    [description, documents]
  );

  return (
    <Flex width="full" flex={3} alignItems="start" flexDirection="column">
      <Box width="full" shadow="inner" rounded="lg" pt="0" px="4" pb="2">
        <Box ref={handleRef} maxHeight={isCollapsed ? "8rem" : undefined} overflow="hidden">
          {tipTapElement}
        </Box>
        {!!description && contentOverflows && !alwaysExpanded && (
          <Button size="sm" variant="link" mt="5px" onClick={() => setShowFullDescription(!showFullDescription)}>
            {!showFullDescription ? t("Show more") : t("Show less")}
          </Button>
        )}
      </Box>
    </Flex>
  );
};
